/**
 * NATIVE WISHLIST ENGINE WITH DYNAMIC JSON CARD RENDERING
 * Prevents full HTML page nesting issues.
 */

class WishlistManager {
  static STORAGE_KEY = 'lakshiv_wishlist_items';

  static getItems() {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  static toggleItem(productHandle) {
    let items = this.getItems();
    const index = items.indexOf(productHandle);
    let added = false;

    if (index > -1) {
      items.splice(index, 1);
    } else {
      items.push(productHandle);
      added = true;
    }

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    this.updateBadges();
    this.syncButtons(productHandle, added);

    if (window.LakshivApp) {
      window.LakshivApp.constructor.showToast(
        added ? 'Saved to Wishlist ❤️' : 'Removed from Wishlist'
      );
    }

    this.renderWishlistPage();
    return added;
  }

  static isWishlisted(productHandle) {
    return this.getItems().includes(productHandle);
  }

  static updateBadges() {
    const count = this.getItems().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  static syncButtons(productHandle, isAdded) {
    document.querySelectorAll(`[data-wishlist-handle="${productHandle}"]`).forEach(btn => {
      btn.classList.toggle('is-active', isAdded);
    });
  }

  static async renderWishlistPage() {
    const grid = document.querySelector('[data-wishlist-grid]');
    const emptyState = document.querySelector('[data-wishlist-empty]');
    if (!grid) return;

    const items = this.getItems();
    if (!items || items.length === 0) {
      grid.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    grid.style.display = 'grid';
    grid.innerHTML = '<div style="grid-column:1/-1;text-align:center;padding:40px;color:var(--color-text-muted);">Loading your saved items...</div>';

    try {
      const promises = items.map(handle => 
        fetch(`/products/${handle}.js`)
          .then(res => {
            if (!res.ok) throw new Error();
            return res.json();
          })
          .catch(() => null)
      );
      
      const products = await Promise.all(promises);
      const validProducts = products.filter(p => p !== null);

      if (validProducts.length > 0) {
        grid.innerHTML = validProducts.map(product => {
          const priceFormatted = `Rs. ${(product.price / 100).toFixed(2)}`;
          const comparePriceHtml = product.compare_at_price > product.price 
            ? `<span class="product-card__compare-price">Rs. ${(product.compare_at_price / 100).toFixed(2)}</span>` 
            : '';
          const savePercentHtml = product.compare_at_price > product.price
            ? `<span class="product-card__discount-tag">SAVE ${Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)}%</span>`
            : '';

          return `
            <div class="product-card" data-product-handle="${product.handle}">
              <div class="product-card__media-wrapper">
                <a href="${product.url}" aria-label="${product.title}">
                  <img src="${product.featured_image}" alt="${product.title}" class="product-card__image" loading="lazy">
                </a>
                <button type="button" class="product-card__wishlist is-active" onclick="WishlistManager.toggleItem('${product.handle}')">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="#E11D48" stroke="#E11D48" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                </button>
              </div>
              <div class="product-card__content">
                <div class="product-card__vendor">${product.vendor || 'LAKSHIV'}</div>
                <h3 class="product-card__title"><a href="${product.url}">${product.title}</a></h3>
                
                <div class="product-card__rating">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <span class="product-card__rating-count">4.9 (128)</span>
                </div>

                <div class="product-card__price-wrapper">
                  <div class="product-card__price-row">
                    <span class="product-card__price">${priceFormatted}</span>
                    ${comparePriceHtml}
                    ${savePercentHtml}
                  </div>
                  <div class="product-card__price-extra">
                    <span>Inclusive of GST</span>
                  </div>
                </div>

                <button type="button" class="product-card__add-btn" data-quick-add-id="${product.variants[0].id}">
                  ADD TO CART
                </button>
              </div>
            </div>
          `;
        }).join('');
      } else {
        grid.style.display = 'none';
        if (emptyState) emptyState.style.display = 'block';
      }
    } catch (e) {
      console.error('Wishlist render error:', e);
    }
  }

  static init() {
    this.updateBadges();
    
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-handle]');
      if (btn) {
        e.preventDefault();
        const handle = btn.getAttribute('data-wishlist-handle');
        this.toggleItem(handle);
      }
    });

    this.getItems().forEach(handle => {
      this.syncButtons(handle, true);
    });

    this.renderWishlistPage();
  }
}

// Bind to window for inline onclick accessibility
window.WishlistManager = WishlistManager;

document.addEventListener('DOMContentLoaded', () => {
  WishlistManager.init();
});
